import { useEffect, useState } from "react";
import { toast } from "sonner";
import { videoService } from "../api/video.api";

interface WaitingRoomToggleProps {
  roomId: string;
  initial: boolean;
  onChange?: (next: boolean) => void;
}

/**
 * Owner-only switch that flips Room.waiting_room_enabled via PATCH /rooms/<id>/.
 * Optimistic UI — toggle moves immediately, reverts on failure with a toast.
 *
 * Matches the design's own pill-switch (weco2/call.jsx's `waitingRoomOn`
 * toggle) — not a native checkbox. Indentation/dimming when guests are
 * disabled is applied by the parent (`InCallInviteContent`), not this
 * component — it always renders for an owner.
 *
 * The thumb's base position is an explicit `start-0.5` (not left
 * implicit/auto) — see `AllowGuestsToggle.tsx` for why an unanchored
 * `translate-x` alone renders the thumb overlapping the track's end edge
 * instead of sliding between the two ends. Same `start-0.5` +
 * `ltr:translate-x-4 rtl:-translate-x-4` convention as the existing
 * autoreply toggle in `modules/email/settings/internal/AutoreplySettings.tsx`.
 */
export function WaitingRoomToggle({
  roomId,
  initial,
  onChange,
}: WaitingRoomToggleProps) {
  const [enabled, setEnabled] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEnabled(initial);
  }, [initial]);

  const handleChange = async () => {
    if (busy) return;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      await videoService.updateRoom(roomId, { waiting_room_enabled: next });
      onChange?.(next);
    } catch (err) {
      console.error("WaitingRoomToggle updateRoom failed:", err);
      setEnabled(!next);
      toast.error("Could not update waiting room settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <button
        type="button"
        onClick={() => void handleChange()}
        disabled={busy}
        aria-pressed={enabled}
        aria-label="Enable waiting room"
        className={`mt-0.5 w-9 h-5 rounded-full shrink-0 relative transition-colors ${enabled ? "bg-primary" : "bg-muted"}`}
      >
        <span
          className={`absolute start-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? "ltr:translate-x-4 rtl:-translate-x-4" : ""}`}
        />
      </button>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-foreground">Waiting room</span>
        <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">
          Guests enter a waiting room and must be admitted by a host.
        </span>
      </span>
    </label>
  );
}
